import { AlphaFilter, Graphics } from 'pixi.js';
import type { ChargedAttack } from '../../data/full/echassier';
import { clamp01, lerp, smoothstep } from '../../shared/scalar';
import { chargeShare } from '../../sim/full/attacks/charged';
import { STILT } from '../../sim/full/skills/grand-pas';
import type { PlayerState, PropState, SimEvent, SimState } from '../../sim/state';
import type { Family, RenderContext } from '../context';
import type { Frame } from '../frame';
import { mixColor } from '../palette';
import { TAU } from '../paint';
import { REFERENCE_RADIUS } from '../textures-looks';
import { byId, lookup } from '../util';
import { NAVY, OUT } from './echassier-paint';
import {
  SHADOW_ALPHA,
  type StiltFoot,
  type StiltPose,
  drawStiltShadow,
  lightAngle,
} from './stilt-shadow';

const FEET_ACROSS = 6;
// His stilts stay under him: walking fast, they slide rather than reach out of his drawing.
const STEP_REACH = 22;
const STEP_LIFT = 7;
const MOVING_RATE = 0.5;
// The stride: the first stilt shoots to where he lands, the second follows over the last part.
const PLANT = { share: 0.2, lift: 25 } as const;
const FOLLOW = { from: 0.42, lift: 40, ease: 1.6 } as const;
const LEG = { out: 7.5, wood: 5, stripe: 1.4, foot: 3.2, least: 10 } as const;
const PRINT = { width: 6, height: 4.5, ring: 8, beats: 1.6 } as const;
const AIM = {
  dashes: 14,
  from: 0.7,
  alpha: 0.6,
  width: 2.5,
  fill: 0.08,
  turnsPerBeat: 0.12,
} as const;
const LANDING = { beats: 0.9, grow: 0.45, from: 10, puffs: 16, dust: 0xefe2c6 } as const;
const STARS = { count: 3, orbit: 1.1, tilt: 0.35, above: 5, size: 2.4, turnsPerBeat: 0.7 } as const;
const STUN_NEAR = 40;
// His charge fills his shadow from his feet up to his hat.
const FILL = { alpha: 0.85, white: 0.45, beatShare: 0.5 } as const;

interface Walk {
  heading: number;
  moving: number;
  speed: number;
  last: number;
}

interface Stride {
  readonly fromX: number;
  readonly fromY: number;
  readonly total: number;
  readonly playerId: number;
  x: number;
  y: number;
  seen: boolean;
}

interface Landing {
  readonly x: number;
  readonly y: number;
  readonly at: number;
  readonly radius: number;
  readonly scale: number;
  readonly color: number;
  readonly prints: readonly [StiltFoot, StiltFoot];
}

function ease(share: number): number {
  return smoothstep(clamp01(share));
}

export function createStilts(ctx: RenderContext): Family {
  const { layers } = ctx;
  const striders = new Map<string, ChargedAttack>();
  for (const [classId, attack] of ctx.attacks) {
    if (attack.kind === 'charged') {
      striders.set(classId, attack);
    }
  }
  const shade = new Graphics();
  shade.filters = [
    new AlphaFilter({ alpha: SHADOW_ALPHA, resolution: 'inherit', antialias: 'inherit' }),
  ];
  const charge = new Graphics();
  charge.filters = [
    new AlphaFilter({ alpha: FILL.alpha, resolution: 'inherit', antialias: 'inherit' }),
  ];
  const marks = new Graphics();
  layers.dim.addChild(shade, charge, marks);
  const legs = new Graphics();
  layers.weapons.addChild(legs);
  const fx = new Graphics();
  layers.fx.addChild(fx);
  const walks = new Map<number, Walk>();
  const strides = new Map<number, Stride>();
  const landings: Landing[] = [];
  const stunned = new Set<number>();
  const feet: [StiltFoot, StiltFoot] = [
    { x: 0, y: 0, lift: 0 },
    { x: 0, y: 0, lift: 0 },
  ];

  function radiusOf(classId: string): number {
    const effect = ctx.skillEffects.get(classId);
    return effect?.kind === 'grandPas' ? effect.radius : 0;
  }

  function colorOf(player: PlayerState, frame: Frame): number {
    return frame.palette[lookup(ctx.classTokens, player.classId, 'class')];
  }

  function walkOf(player: PlayerState, frame: Frame): Walk {
    let walk = walks.get(player.id);
    if (walk === undefined) {
      walk = {
        heading: Math.atan2(player.aim.y, player.aim.x),
        moving: 0,
        speed: 0,
        last: frame.now,
      };
      walks.set(player.id, walk);
    }
    const dt = Math.min(4, Math.max(0, frame.now - walk.last));
    walk.last = frame.now;
    const moved = Math.hypot(player.x - player.prevX, player.y - player.prevY);
    if (moved > 1e-3 && player.dash === undefined) {
      walk.heading = Math.atan2(player.y - player.prevY, player.x - player.prevX);
    }
    const share = 1 - Math.exp(-dt * MOVING_RATE);
    const walking = player.dash === undefined && moved > 0.15 * player.speed;
    walk.moving += ((walking ? 1 : 0) - walk.moving) * share;
    walk.speed += ((walking ? Math.min(moved, player.speed) : 0) - walk.speed) * share;
    return walk;
  }

  // One step a beat for each stilt: a beat planted while he walks over it, a beat swinging ahead.
  function walkingFeet(x: number, y: number, scale: number, walk: Walk, frame: Frame): void {
    const beats = (frame.now - frame.tempo.originTick) / frame.tempo.ticksPerBeat;
    const stride =
      Math.min(walk.speed * frame.tempo.ticksPerBeat, STEP_REACH * scale) * walk.moving;
    const alongX = Math.cos(walk.heading);
    const alongY = Math.sin(walk.heading);
    feet.forEach((foot, index) => {
      const turn = beats - index + 2;
      const phase = turn - 2 * Math.floor(turn / 2);
      const swinging = phase < 1;
      const along = swinging
        ? lerp(-0.5 * stride, 1.5 * stride, smoothstep(phase)) - stride * phase
        : 1.5 * stride - stride * phase;
      const across = (index === 0 ? -1 : 1) * FEET_ACROSS * scale;
      foot.x = x + alongX * along - alongY * across;
      foot.y = y + alongY * along + alongX * across;
      foot.lift = swinging ? Math.sin(phase * Math.PI) * STEP_LIFT * scale * walk.moving : 0;
    });
  }

  function strideOf(prop: PropState, owner: PlayerState): Stride {
    let stride = strides.get(prop.id);
    if (stride === undefined) {
      stride = {
        fromX: owner.prevX,
        fromY: owner.prevY,
        total: prop.ticksLeft,
        playerId: owner.id,
        x: prop.x,
        y: prop.y,
        seen: true,
      };
      strides.set(prop.id, stride);
    }
    stride.seen = true;
    return stride;
  }

  function strideFeet(stride: Stride, prop: PropState, scale: number, alpha: number): number {
    const share = clamp01((stride.total - prop.ticksLeft + alpha) / stride.total);
    const dx = stride.x - stride.fromX;
    const dy = stride.y - stride.fromY;
    const length = Math.hypot(dx, dy) || 1;
    const acrossX = (-dy / length) * FEET_ACROSS * scale;
    const acrossY = (dx / length) * FEET_ACROSS * scale;
    const planted = clamp01(share / PLANT.share);
    const follow = clamp01((share - FOLLOW.from) / (1 - FOLLOW.from));
    const followed = 1 - (1 - smoothstep(follow)) ** FOLLOW.ease;
    const [first, second] = feet;
    first.x = lerp(stride.fromX, stride.x, ease(planted)) - acrossX;
    first.y = lerp(stride.fromY, stride.y, ease(planted)) - acrossY;
    first.lift = Math.sin(Math.PI * planted) * PLANT.lift * scale;
    second.x = lerp(stride.fromX, stride.x, followed) + acrossX;
    second.y = lerp(stride.fromY, stride.y, followed) + acrossY;
    second.lift = Math.sin(Math.PI * follow) * FOLLOW.lift * scale;
    return share;
  }

  function drawLegs(x: number, y: number, scale: number, color: number): void {
    for (const foot of feet) {
      if (Math.hypot(foot.x - x, foot.y - y) < LEG.least * scale) {
        continue;
      }
      for (const [width, tint] of [
        [LEG.out, OUT],
        [LEG.wood, NAVY],
        [LEG.stripe, color],
      ] as const) {
        legs
          .moveTo(x, y)
          .lineTo(foot.x, foot.y)
          .stroke({ width: width * scale, color: tint, cap: 'round' });
      }
      legs.circle(foot.x, foot.y, LEG.foot * scale).fill({ color: NAVY });
      legs.circle(foot.x, foot.y, LEG.foot * scale).stroke({ width: scale, color: OUT });
    }
  }

  function drawPrint(x: number, y: number, scale: number, color: number, alpha: number): void {
    marks
      .ellipse(x, y, PRINT.width * scale, PRINT.height * scale)
      .fill({ color: 0, alpha: 0.55 * alpha });
    marks.circle(x, y, PRINT.ring * scale).stroke({ width: 2 * scale, color, alpha });
  }

  function drawAim(
    x: number,
    y: number,
    radius: number,
    share: number,
    color: number,
    frame: Frame,
  ) {
    const shown = ease(share / PLANT.share);
    if (shown <= 0) {
      return;
    }
    const beats = frame.calm ? 0 : (frame.now - frame.tempo.originTick) / frame.tempo.ticksPerBeat;
    const ring = radius * (AIM.from + (1 - AIM.from) * shown);
    marks.circle(x, y, radius).fill({ color, alpha: AIM.fill * shown });
    const turn = beats * AIM.turnsPerBeat * TAU;
    for (let index = 0; index < AIM.dashes; index += 1) {
      const start = turn + (index * TAU) / AIM.dashes;
      marks
        .moveTo(x + Math.cos(start) * ring, y + Math.sin(start) * ring)
        .arc(x, y, ring, start, start + (0.45 * TAU) / AIM.dashes)
        .stroke({ width: AIM.width, color, alpha: AIM.alpha * shown });
    }
  }

  function drawLanding(landing: Landing, frame: Frame): boolean {
    const beats = (frame.now - landing.at) / frame.tempo.ticksPerBeat;
    const printFade = 1 - clamp01(beats / PRINT.beats);
    for (const print of landing.prints) {
      drawPrint(print.x, print.y, landing.scale, landing.color, printFade);
    }
    if (beats >= LANDING.beats) {
      return printFade > 0;
    }
    const life = beats / LANDING.beats;
    const grown = LANDING.from + (landing.radius - LANDING.from) * ease(beats / LANDING.grow);
    const strength = (1 - life) * (frame.calm ? 0.55 : 1);
    fx.circle(landing.x, landing.y, grown).fill({ color: landing.color, alpha: 0.14 * strength });
    fx.circle(landing.x, landing.y, grown).stroke({
      width: 3,
      color: landing.color,
      alpha: strength,
    });
    fx.circle(landing.x, landing.y, grown - 3).stroke({
      width: 1,
      color: 0xffffff,
      alpha: 0.5 * strength,
    });
    if (!frame.calm) {
      for (let index = 0; index < LANDING.puffs; index += 1) {
        const angle = (index / LANDING.puffs) * TAU + (index % 3) * 0.17;
        const reach = landing.radius * (0.55 + (0.45 * ((index * 7) % 5)) / 4) * ease(life) + 3;
        fx.circle(
          landing.x + Math.cos(angle) * reach,
          landing.y + Math.sin(angle) * reach,
          (1.8 + (index % 4) * 0.6) * (1 - 0.5 * life),
        ).fill({ color: LANDING.dust, alpha: 0.75 * (1 - life) });
      }
    }
    return true;
  }

  function drawCharge(
    player: PlayerState,
    attack: ChargedAttack,
    pose: StiltPose,
    color: number,
    frame: Frame,
  ): void {
    const share = chargeShare(player, attack);
    const beats = (frame.now - frame.tempo.originTick) / frame.tempo.ticksPerBeat;
    const beat = frame.calm ? 0 : 1 - smoothstep(clamp01((beats - Math.floor(beats)) * 2));
    const full = share >= 1;
    const lit = full
      ? mixColor(color, 0xffffff, FILL.white * (1 - FILL.beatShare * (1 - beat)))
      : color;
    if (share > 0) {
      drawStiltShadow(charge, { ...pose, upTo: share, color: lit });
    }
  }

  function drawStars(state: SimState, alpha: number, frame: Frame): void {
    const { now, calm, palette, tempo } = frame;
    const turn = calm ? 0 : (now / tempo.ticksPerBeat) * STARS.turnsPerBeat * TAU;
    for (const id of stunned) {
      const enemy = byId(state.enemies, id);
      if (enemy === undefined || enemy.hp <= 0 || enemy.statuses?.frozen === undefined) {
        stunned.delete(id);
        continue;
      }
      const x = lerp(enemy.prevX, enemy.x, alpha);
      const y = lerp(enemy.prevY, enemy.y, alpha);
      for (let index = 0; index < STARS.count; index += 1) {
        const angle = turn + (index * TAU) / STARS.count;
        fx.star(
          x + Math.cos(angle) * enemy.radius * STARS.orbit,
          y - enemy.radius - STARS.above + Math.sin(angle) * enemy.radius * STARS.tilt,
          4,
          STARS.size,
          STARS.size * 0.45,
        ).fill({ color: palette.noyau });
      }
    }
  }

  return {
    onEvent(event: SimEvent, state: SimState): void {
      if (event.type !== 'enemyStatusStarted' || event.status !== 'frozen') {
        return;
      }
      const landed = [...strides].some(([id, stride]) => {
        const owner = byId(state.players, stride.playerId);
        if (owner === undefined || byId(state.props ?? [], id) !== undefined) {
          return false;
        }
        const reach = radiusOf(owner.classId) + STUN_NEAR;
        return Math.hypot(event.x - owner.x, event.y - owner.y) <= reach;
      });
      if (landed) {
        stunned.add(event.id);
      }
    },
    reset(): void {
      walks.clear();
      strides.clear();
      landings.length = 0;
      stunned.clear();
      shade.clear();
      charge.clear();
      marks.clear();
      legs.clear();
      fx.clear();
    },
    update(state: SimState, alpha: number, frame: Frame): void {
      shade.clear();
      charge.clear();
      marks.clear();
      legs.clear();
      fx.clear();
      const light = lightAngle(frame.fraction);
      const lightX = Math.cos(light);
      const lightY = Math.sin(light);
      const beats = (frame.now - frame.tempo.originTick) / frame.tempo.ticksPerBeat;
      for (const stride of strides.values()) {
        stride.seen = false;
      }
      for (const player of state.players) {
        const attack = striders.get(player.classId);
        if (attack === undefined || player.downed) {
          continue;
        }
        const x = lerp(player.prevX, player.x, alpha);
        const y = lerp(player.prevY, player.y, alpha);
        const scale = player.radius / REFERENCE_RADIUS;
        const color = colorOf(player, frame);
        const walk = walkOf(player, frame);
        const prop = state.props?.find((one) => one.kind === STILT && one.playerId === player.id);
        if (prop === undefined) {
          walkingFeet(x, y, scale, walk, frame);
        } else {
          const stride = strideOf(prop, player);
          const share = strideFeet(stride, prop, scale, alpha);
          drawAim(prop.x, prop.y, radiusOf(player.classId), share, color, frame);
          if (share >= PLANT.share) {
            drawPrint(feet[0].x, feet[0].y, scale, color, 1);
          }
          drawLegs(x, y, scale, color);
        }
        const pose: StiltPose = {
          x,
          y,
          scale,
          facing: Math.atan2(player.aim.y, player.aim.x),
          lightX,
          lightY,
          feet,
          beats,
          calm: frame.calm,
          length: 1,
        };
        drawStiltShadow(shade, pose);
        if (player.chargeTicks !== undefined) {
          drawCharge(player, attack, pose, color, frame);
        }
      }
      for (const [id, stride] of strides) {
        if (stride.seen) {
          continue;
        }
        strides.delete(id);
        const owner = byId(state.players, stride.playerId);
        if (owner === undefined || owner.downed) {
          continue;
        }
        const scale = owner.radius / REFERENCE_RADIUS;
        const dx = stride.x - stride.fromX;
        const dy = stride.y - stride.fromY;
        const length = Math.hypot(dx, dy) || 1;
        const acrossX = (-dy / length) * FEET_ACROSS * scale;
        const acrossY = (dx / length) * FEET_ACROSS * scale;
        landings.push({
          x: owner.x,
          y: owner.y,
          at: frame.now,
          radius: radiusOf(owner.classId),
          scale,
          color: colorOf(owner, frame),
          prints: [
            { x: owner.x - acrossX, y: owner.y - acrossY, lift: 0 },
            { x: owner.x + acrossX, y: owner.y + acrossY, lift: 0 },
          ],
        });
      }
      let kept = 0;
      for (const landing of landings) {
        if (drawLanding(landing, frame)) {
          landings[kept] = landing;
          kept += 1;
        }
      }
      landings.length = kept;
      drawStars(state, alpha, frame);
    },
    destroy(): void {
      shade.destroy();
      charge.destroy();
      marks.destroy();
      legs.destroy();
      fx.destroy();
    },
  };
}
